import { Col, Row } from 'antd';
import { H5 } from 'components/ui/base/typography';
import SelectTag from 'components/ui/composed/selectTag/SelectTag';
import { FormikProvider, useFormik } from 'formik';
import useProductsTemplates from 'hooks/useProductsTemplates';
import useProductTemplate from 'hooks/useProductTemplate';
import useProductTemplateTags from 'hooks/useProductTemplateTags';
import useFormUtils from 'hooks/useFormUtils';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { removeEmptyObjectsFromDeclarationArrays } from 'views/declarations/utils/form-utils';
import { StyledHeader, StyledLayout } from './Products.styles';
import { useTranslation } from 'react-i18next';
import useDeclarationNotifications from 'hooks/useDeclarationNotifications';
import { isEmpty, startCase } from 'lodash';
import useGlobalOverlay from '../../../hooks/useGlobalOverlay';
import useProductTemplateFormErrors from '../../../hooks/useProductTemplateFormErrors';
import { DeclarationInternalType } from '../../../store/declarations/enums/common/declaration-internal-type';
import { productTypeMapHelpers, ProductTypeMapParams } from './productTemplateMap';
import { Box44Context } from '../../declarations/common/box44/Box44';
import { useRequestPromise } from 'hooks/useRequest';
import axiosClient from 'config/axios';
import config from 'config';
import { TemplateResponse } from 'store/template/template';
import validate, { FormModel, transformErrorsForFormik } from 'views/declarations/uk/export/validations/validations';
import { DeclarationContextProvider } from 'views/declarations/common/DeclarationContext';
import DeclarationInformation from 'views/declarations/common/DeclarationInformation';
import { ProductTemplateContext } from 'utils/ProductTemplateContext';
import useGetDeclarationMapValues from '../../../hooks/useGetDeclarationMapValues';
import { box44PathAndFieldNames } from '../../declarations/common/box44/mappers';
import { DeclarationTypes, MessageTypes } from '../../../store/declarations/enums/common/declaration-types';
import {
    getDeclarationTypeByProductTemplate,
    getDeclarationTypeByTemplate,
} from 'views/declarations/utils/declaration-utils';
import useCodelists from 'hooks/useCodelists';
import DeclarationFormContent from 'views/declarations/declaration-tabs/declaration-form/DeclarationFormContent';
import { StyledButton, Tags } from 'views/declarations/declaration-tabs/declaration-form/Form.styles';
import { TransformData } from 'views/declarations/declaration-tabs/declaration-form/DeclarationFormTab';
import useBreadcrumb from 'hooks/useBreadcrumb';

interface LocationState {
    teamId?: string;
}

const AddEditProductTemplate: FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation('common');
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { showErrorNotification } = useDeclarationNotifications();
    const { setFormProductTemplateErrors, clearFormProductTemplateErrors } = useProductTemplateFormErrors();

    const location = useLocation();
    const { id, type, country, internalType } = useParams<{
        id: string;
        type: MessageTypes;
        country: DeclarationCountry;
        internalType: DeclarationInternalType;
    }>();

    const { saveProductTemplate, editProductTemplate } = useProductsTemplates();
    const { countryLowerCase, internalType: formInternalType, formType } = useFormUtils();
    const productIdentity = {
        country: countryLowerCase as DeclarationCountry,
        internalType: formInternalType as DeclarationInternalType,
        messageType: formType as MessageTypes,
    };
    const productTemplate = useProductTemplate({ productId: id, ...productIdentity });
    const tagsList = useProductTemplateTags(productIdentity);

    const declarationViewMapValues = useGetDeclarationMapValues();
    const { getCodeLists } = useCodelists();
    const teamId = (location.state as LocationState)?.teamId;

    const transform = useMemo(
        () => declarationViewMapValues?.transformData as TransformData,
        [declarationViewMapValues]
    );

    const [searchParams] = useSearchParams();

    const declarationTemplateId = useMemo(
        () => searchParams.get('declarationTemplateId') ?? productTemplate?.declarationTemplateId,
        [productTemplate?.declarationTemplateId, searchParams]
    );

    const { data: declarationTemplate, refetch } = useRequestPromise(
        async () => {
            if (!declarationTemplateId) return undefined;
            return axiosClient
                .get<{ payload: TemplateResponse }>(`${config.declarationTemplatesUrl}/${declarationTemplateId}`)
                .then((res) => res.data.payload);
        },
        { loadingMessage: 'Getting declaration template data' }
    );

    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [declarationTemplateId]);

    useEffect(() => {
        const hasProductTemplate = Boolean(productTemplate?.type);
        const hasCustomer = Boolean(declarationTemplate?.customerId);

        const declarationType =
            hasProductTemplate && hasCustomer
                ? getDeclarationTypeByProductTemplate(productTemplate?.type)
                : getDeclarationTypeByTemplate(country?.toUpperCase(), internalType);

        const resolvedCustomerId = hasProductTemplate && hasCustomer ? declarationTemplate?.customerId : undefined;

        getCodeLists(country as string, declarationType as DeclarationTypes, resolvedCustomerId);
    }, [country, productTemplate?.type, declarationTemplate?.customerId, internalType, getCodeLists]);

    const [initialValues, setInitialValues] = useState<any>({});
    const [suggestedTags, setSuggestedTags] = useState<string[]>(tagsList);
    const [usedTags, setUsedTags] = useState<string[]>([]);

    const typeMap = useMemo(() => {
        if (!country || !internalType) throw new Error('No country and internal type');

        const typeMap = productTypeMapHelpers[country][internalType];
        if (!typeMap) throw new Error('Fill the type map correctly');

        let final = typeMap.default;

        if (type && final) final = Object.assign(final, typeMap[type]);

        return final as ProductTypeMapParams;
    }, [country, internalType, type]);

    const formikInitialValues = useMemo(() => {
        const _productTemplateData = declarationTemplate?.template?.product.defaults;
        const productTemplateData =
            country === 'uk' && internalType !== DeclarationInternalType.NCTS
                ? _productTemplateData?.governmentAgencyGoodsItem
                : _productTemplateData;
        if (!id) return transform?.product?.forClient?.(productTemplateData, true) ?? {};

        return initialValues;
    }, [declarationTemplate?.template?.product.defaults, country, id, transform?.product, initialValues, internalType]);

    useEffect(() => {
        clearFormProductTemplateErrors();

        return () => clearFormProductTemplateErrors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (tagsList && id) {
            setSuggestedTags(Array.from(new Set([...tagsList, ...usedTags])));
        } else if (tagsList) {
            setSuggestedTags(tagsList);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tagsList, id]);

    useEffect(() => {
        if (productTemplate && id) {
            const initialValues = transform ? transform?.product?.forClient?.(productTemplate, true) : productTemplate;

            setInitialValues(initialValues);
            setUsedTags(productTemplate.tags ?? []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productTemplate, id, transform]);

    useEffect(() => {
        setBreadcrumbRoutes([
            {
                breadcrumbName: 'Templates',
                path: '/templates',
            },
            {
                breadcrumbName: 'Product Template Builder',
                path: '',
            },
        ]);
    }, [setBreadcrumbRoutes]);

    const formik = useFormik({
        initialValues: formikInitialValues,
        enableReinitialize: true,
        validateOnMount: true,
        validateOnChange: false,
        validationSchema: typeMap.validationSchema,
        validate:
            typeMap.validate &&
            (async (values) => transformErrorsForFormik(await validate(new FormModel(values), typeMap.validate))),
        onSubmit: () => {},
    });

    const handleFormSubmit = useCallback(async () => {
        let formValues = removeEmptyObjectsFromDeclarationArrays({ ...formik.values });

        if (declarationTemplateId) formValues = { ...formValues, declarationTemplateId };

        let transformedForServer = transform ? transform?.product?.forServer?.(formValues, true) : formValues;

        if (country === DeclarationCountry.UK && internalType !== DeclarationInternalType.NCTS)
            transformedForServer = transformedForServer.governmentAgencyGoodsItem;

        const addOrEditParams: any = productTemplate?.id
            ? { ...transformedForServer, id: productTemplate.id }
            : transformedForServer;

        const response = await (id ? editProductTemplate : saveProductTemplate)({
            ...addOrEditParams,
            tags: usedTags,
            teamId,
        });

        response && navigate(`/templates?tab=product`);
    }, [
        formik.values,
        declarationTemplateId,
        transform,
        country,
        internalType,
        productTemplate?.id,
        id,
        editProductTemplate,
        saveProductTemplate,
        usedTags,
        teamId,
        navigate,
    ]);

    const handleValidateForm = useCallback(async () => {
        const errors = await formik.validateForm();

        if (isEmpty(errors)) {
            clearFormProductTemplateErrors();
            return true;
        }

        formik.setTouched(errors as any);
        if (typeMap.validationErrorsParser) setFormProductTemplateErrors(typeMap.validationErrorsParser(errors));
        return false;
    }, [formik, typeMap, clearFormProductTemplateErrors, setFormProductTemplateErrors]);

    const hasAdditionalInformationFields =
        internalType === DeclarationInternalType.IMPORT && country === DeclarationCountry.IRELAND;

    return (
        <FormikProvider value={formik}>
            <ProductTemplateContext.Provider value={{ declarationTemplate, inProductTemplate: true }}>
                <StyledLayout style={{ height: '100%' }}>
                    <StyledHeader style={{ height: 'auto' }}>
                        <Box44Context.Provider
                            value={{
                                documentTypeName: box44PathAndFieldNames[type as MessageTypes]?.documentTypeName,
                                documentIdentifierName:
                                    box44PathAndFieldNames[type as MessageTypes]?.documentIdentifierName,
                                monetaryAmountName: box44PathAndFieldNames[type as MessageTypes]?.monetaryAmountName,
                                measurementName: box44PathAndFieldNames[type as MessageTypes]?.measurementName,
                                // Other props
                                path: box44PathAndFieldNames[type as MessageTypes]?.productPath,
                                hasAdditionalInfo: hasAdditionalInformationFields,
                                messageType: type,
                                isProduct: true,
                            }}
                        >
                            <Row gutter={10} style={{ marginBottom: '1.8rem' }} justify="space-between" align="top">
                                <Col
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '16px',
                                    }}
                                >
                                    <H5
                                        style={{
                                            fontSize: '24px',
                                        }}
                                    >
                                        {country === DeclarationCountry.IRELAND ? 'Irish' : country?.toUpperCase()}{' '}
                                        {startCase(internalType?.toLowerCase())} {type} Product
                                    </H5>
                                    <Tags>
                                        <SelectTag
                                            usedTags={usedTags}
                                            suggestedTags={suggestedTags}
                                            onSuggestedTagsChange={(value: string[]) => setSuggestedTags(value)}
                                            onUsedTagsChange={(value: string[]) => setUsedTags(value)}
                                        />
                                    </Tags>
                                    <DeclarationInformation
                                        visibleInfo={{
                                            template: true,
                                            jobReferences: false,
                                            box44:
                                                country === DeclarationCountry.IRELAND &&
                                                (internalType === 'IMPORT' || internalType === 'ENS'),
                                        }}
                                    />
                                </Col>
                                <Col
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    <StyledButton
                                        onClick={() => {
                                            navigate('/templates?tab=product');
                                        }}
                                    >
                                        {t('buttons.cancel')}
                                    </StyledButton>
                                    <StyledButton
                                        type="primary"
                                        primary
                                        onClick={async () => {
                                            showGlobalOverlay({
                                                type: 'LoadingOverlay',
                                            });

                                            const isValid = await handleValidateForm();

                                            if (!isValid) {
                                                showErrorNotification(
                                                    t('error.product_template_invalid_title'),
                                                    t('error.formRequired')
                                                );
                                                hideGlobalOverlay();
                                                return;
                                            }

                                            handleFormSubmit();

                                            hideGlobalOverlay();
                                        }}
                                    >
                                        {t('footer.save')}
                                    </StyledButton>
                                </Col>
                            </Row>
                        </Box44Context.Provider>
                    </StyledHeader>
                    <DeclarationContextProvider>
                        <DeclarationFormContent>
                            <typeMap.productSection formik={formik} productTemplate />
                        </DeclarationFormContent>
                    </DeclarationContextProvider>
                </StyledLayout>
            </ProductTemplateContext.Provider>
        </FormikProvider>
    );
};

export default AddEditProductTemplate;
